import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import {
  ClientBranding,
  ClientBrandingSplash,
} from '@goalsetter/classroom-types/academy';
import {
  useBrandingConfig,
  BrandingConfigData,
} from '@/hooks/useBrandingConfig';
import { BrandingImageType } from '@/components/BrandingImage';

const pickTrimmedUrl = (value: string | undefined): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Login co-brand row only when `brandingLogos` exists: `secondary` → `main`.
 * If `brandingLogos` is absent (non co-branded tenant), returns `null` — no `iconVersions` fallback.
 */
const resolveTenantLogoUrlForLogin = (
  branding: ClientBranding | undefined,
): string | null => {
  const logos = branding?.brandingLogos;
  if (!logos) {
    return null;
  }
  const fromMain = pickTrimmedUrl(logos.main);
  if (fromMain !== undefined) {
    return fromMain;
  }
  const fromSecondary = pickTrimmedUrl(logos.secondary);
  if (fromSecondary !== undefined) {
    return fromSecondary;
  }
  return null;
};

interface BrandingContextType extends BrandingConfigData {
  getClientName: () => string | null;
  getClientCode: () => string | null;
  getBrandingLogo: (type?: BrandingImageType) => string | null;
  /** Resolved co-brand logo for login when `brandingLogos` exists (secondary → main). */
  tenantLogoUrl: string | null;
  getStylesVersion: () => string | null;
  getSplash: () => ClientBrandingSplash | null;
  hasBranding: boolean;
  hasConfig: boolean;
  hasSplash: boolean;
}

const BrandingContext = createContext<BrandingContextType | null>(null);

interface BrandingProviderProps {
  children: ReactNode;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({
  children,
}) => {
  const brandingData = useBrandingConfig();

  const getClientName = (): string | null => {
    return (
      brandingData.config?.name ||
      brandingData.branding?.name ||
      brandingData.subdomain
    );
  };

  const getClientCode = (): string | null => {
    return (
      brandingData.config?.code ||
      brandingData.branding?.code ||
      brandingData.subdomain
    );
  };

  const getBrandingLogo = useCallback(
    (type: BrandingImageType = BrandingImageType.LOGO): string | null => {
      return (
        (brandingData.branding?.branding as ClientBranding)?.iconVersions?.[
          type
        ] || null
      );
    },
    [brandingData.branding],
  );

  const tenantLogoUrl = useMemo((): string | null => {
    const branding = brandingData.branding?.branding as
      | ClientBranding
      | undefined;
    return resolveTenantLogoUrlForLogin(branding);
  }, [brandingData.branding]);

  const getStylesVersion = (): string | null => {
    return brandingData.branding?.branding?.stylesVersion || null;
  };

  const getSplash = useCallback((): ClientBrandingSplash | null => {
    return (brandingData.branding?.branding as ClientBranding)?.splash || null;
  }, [brandingData.branding]);

  const hasBranding = useMemo((): boolean => {
    return !!brandingData.branding;
  }, [brandingData.branding]);

  const hasConfig = useMemo((): boolean => {
    return !!brandingData.config;
  }, [brandingData.config]);

  const hasSplash = useMemo((): boolean => {
    return !!(brandingData.branding?.branding as ClientBranding)?.splash
      ?.enabled;
  }, [brandingData.branding]);

  const contextValue: BrandingContextType = {
    ...brandingData,
    getClientName,
    getClientCode,
    getBrandingLogo,
    tenantLogoUrl,
    getStylesVersion,
    getSplash,
    hasBranding,
    hasConfig,
    hasSplash,
  };

  return (
    <BrandingContext.Provider value={contextValue}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBrandingContext = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error(
      'useBrandingContext must be used within a BrandingProvider',
    );
  }
  return context;
};
